import { Lease } from '~/types/Lease';
import { Property } from '~/types/Property';
import { Tenant } from '~/types/Tenant';
import { Unit } from '~/types/Unit';
import { getLeaseImage } from '~/utils/leaseUtils';
import { Gateway } from './gateway';
import { authenticatingNetworkManager } from './network/authenticatingNetworkManager';
import { MagicRequest, RequestMethod } from './network/magicRequest';

const LEASES_URL = '/api/leases';

class LeaseGateway extends Gateway {
  public async getLeases(): Promise<Lease[]> {
    const request: MagicRequest = {
      url: LEASES_URL,
      method: RequestMethod.GET,
      headers: this.createCommonHeaders(),
    };
    const response: Response = await this.sendRequest(request);
    const json = await response.json();
    return json.map((jsonLease: any) => {
      const { image, thumbnail } = getLeaseImage(jsonLease);
      return {
        id: jsonLease.id,
        portfolioId: jsonLease.portfolioId,
        propertyId: jsonLease.propertyId,
        unitId: jsonLease.unitId,
        start: jsonLease.start,
        end: jsonLease.end,
        ended: jsonLease.ended,
        monthToMonth: jsonLease.monthToMonth,
        eviction: jsonLease.eviction,
        chatId: jsonLease.chatId,
        tenants: this.getTenantsFromJson(jsonLease.tenants),
        currentRent: jsonLease.currentRent,
        securityDepositPaid: jsonLease.securityDepositPaid,
        securityDepositRequested: jsonLease.securityDepositRequested,
        securityDepositDue: jsonLease.securityDepositDue,
        lastPaymentDate: jsonLease.lastPaymentDate,
        draft: jsonLease.draft,
        status: jsonLease.status,
        property: this.getPropertyFromJson(jsonLease.property),
        unit: this.getUnitFromJson(jsonLease.unit),
        currentBalance: jsonLease.balance,
        postedBalance: jsonLease.postedBalance,
        image,
        thumbnail,
      } as Lease;
    });
  }

  private getPropertyFromJson(json: any): Property {
    return {
      id: json.id,
      address: json.address,
      displayName: json.displayName,
      name: json.name,
      portfolioId: json.portfolioId,
      type: json.type,
      image: {
        url: `/api/properties/${json.id}/image.webp`,
      },
    } as Property;
  }

  private getUnitFromJson(json: any): Unit {
    return {
      id: json.id,
      name: json.name,
      beds: json.beds,
      baths: json.baths,
      unitSizeSqft: json.unitSizeSqft,
      propertyId: json.propertyId,
      portfolioId: json.portfolioId,
      images: [
        {
          signedUrl: `api/units/${json.id}/image.webp`,
          signedThumbUrl: `api/units/${json.id}/image.webp`,
        },
      ],
    } as Unit;
  }

  private getTenantsFromJson(json: any): Tenant[] {
    if (json) {
      return json.map((jsonTenant: any) => {
        return {
          id: jsonTenant.tenant.id,
          email: jsonTenant.tenant.email,
          firstName: jsonTenant.tenant.firstName,
          lastName: jsonTenant.tenant.lastName,
          phoneNumber: jsonTenant.tenant.phone,
          created: jsonTenant.tenant.created,
          updated: jsonTenant.tenant.updated,
          lastSeen: jsonTenant.tenant.lastSeen,
          chatId: jsonTenant.tenant.chatId,
          language: jsonTenant.tenant.language,
          moveIn: jsonTenant.moveIn,
          moveOut: jsonTenant.moveOut,
          hasMovedOut: jsonTenant.isMovedOut,
        } as Tenant;
      });
    }
    return [] as Tenant[];
  }
}

export const leaseGateway = new LeaseGateway(authenticatingNetworkManager);
